// WHAT PAGE IS THIS, IN THE BOOK THE PRINTER MAKES.
//
// ─── WHY ONE FUNCTION ───────────────────────────────────────────────────────
//
// On 2026-09-20 order #1198 was open in three places at once and each gave a
// different answer for the same album:
//
//   editor toolbar   "impr. 4–5/42"   — hand-written `totalPages + 2`
//   editor slider    "Imprimé : 40"   — rounded the CONTENT count to 4 and
//                                       forgot the intro and outro entirely
//   3D preview       "Pages 4–5 / 44" — counted the real PDF
//   the PDF itself    44 pages        — intro + 40 + outro = 42, padded to 44
//
// Three of the four were guesses, each written next to the thing it labelled.
// Nobody was wrong about the rule; they were each carrying their own copy of
// it, and two copies of a rule is one rule and one bug waiting.
//
// The rule, once:
//   the printer images A3 sheets double-sided, 4 album pages to a sheet, so
//   the interior must divide by 4. The book is  intro? + content + outro?,
//   padded up with duplicated outro pages (see interiorPagePadding.ts, which
//   owns the padding arithmetic and is used by the PDF assembler itself).
//   The customer's page N is therefore print page N + (intro ? 1 : 0).
//
// ─── "intro?" IS A FACT ABOUT THE BOOK, NOT ONLY ABOUT THE BRAND ────────────
//
// Since design system v2 §7 S1 (2026-10-14) a THEMED book opens on its own S1
// page and the Storiz intro page is NOT printed (bookStructure.ts). So every
// caller passes  hasIntro = printsIntro(isThemedBook(pages), Boolean(introUrl))
// — `bookPrintsIntro` below — never the bare intro setting. A themed book's
// album page N is print page N; its total is content + outro, padded to 4.
// A plain book is exactly as before.
//
// Guard: renderParity.test.ts — and it checks the call SITES, not just the
// maths, because a correct function nobody calls is what we already had.
// Themed books: bookStructure.print.test.ts.

import { paddedInteriorPageCount } from './interiorPagePadding';
import { isThemedBook, printsIntro, spreadCount, spreadFirstIndex, type BookPageLike } from './bookStructure';

/**
 * Is the Storiz intro page printed in front of THIS book? Only when the brand
 * has one (`introUrl`) and the book is not themed. The one `hasIntro` every
 * function below takes.
 */
export function bookPrintsIntro(
  pages: ReadonlyArray<BookPageLike | null | undefined> | null | undefined,
  introUrl: string | null | undefined,
): boolean {
  return printsIntro(isThemedBook(pages), Boolean(introUrl));
}

/**
 * The page number a customer's album page carries in the printed book and in
 * the 3D preview (which is a rasterisation of that same PDF).
 *
 * `albumPage` is 1-based, as the editor shows it. The Storiz intro page is
 * printed first when the brand has one, so everything after it shifts by one.
 */
export function printPageOf(albumPage: number, hasIntro: boolean): number {
  return Math.max(1, Math.round(albumPage)) + (hasIntro ? 1 : 0);
}

/**
 * How many pages the printed interior really has: the bookends, and the
 * padding the assembler appends to reach a multiple of 4.
 *
 * ⚠️ THIS IS THE NUMBER TO SHOW A CUSTOMER NEXT TO A PRINT PAGE NUMBER. Saying
 * "page 43 of 42" is how the editor and the preview were caught disagreeing.
 */
export function printedInteriorTotal(
  contentPages: number, hasIntro: boolean, hasOutro: boolean,
): number {
  return paddedInteriorPageCount(Math.max(0, Math.round(contentPages)), hasIntro, hasOutro);
}

/**
 * The printed spread a customer's two-page spread becomes: `[left, right]`,
 * clamped to the real printed length.
 */
export function printSpreadOf(
  leftAlbumPage: number, contentPages: number, hasIntro: boolean, hasOutro: boolean,
): [number, number] {
  const total = printedInteriorTotal(contentPages, hasIntro, hasOutro);
  const l = Math.min(printPageOf(leftAlbumPage, hasIntro), total);
  const r = Math.min(printPageOf(leftAlbumPage + 1, hasIntro), total);
  return [l, r];
}

/** One open spread of the printed book, in album pages. */
export interface PrintedSpread<T> {
  left: T | null;
  right: T | null;
  /** 1-based album page numbers (array positions + 1); null only for the
   *  empty left side facing a themed book's opening page. */
  leftNo: number | null;
  rightNo: number;
}

/**
 * The album's pages paired the way the PRINTED book opens.
 *
 *   plain   (1, 2), (3, 4)…           — album page 1 is a left page
 *   themed  (—, 1), (2, 3), (4, 5)…   — the S1 opening stands alone on the right
 *
 * Numbers are array positions + 1, exactly what the flow's cards have always
 * shown, so a plain book's spreads and labels are unchanged.
 */
export function printedSpreads<T>(pages: ReadonlyArray<T>, themed: boolean): PrintedSpread<T>[] {
  const out: PrintedSpread<T>[] = [];
  const at = (i: number): T | null => (i >= 0 && i < pages.length ? pages[i] ?? null : null);
  // The pairing is bookStructure's (spreadCount / spreadFirstIndex) — the same
  // one the editor and the page checker open the book with.
  for (let s = 0; s < spreadCount(pages.length, themed); s++) {
    const i = spreadFirstIndex(s, themed);
    out.push({ left: at(i), right: at(i + 1), leftNo: i >= 0 ? i + 1 : null, rightNo: i + 2 });
  }
  return out;
}
